import React, { useState } from "react";
import { useSelector } from "react-redux";
import logo from "../../../Assets/images/user_square.png";
import { LazyLoadImage } from "react-lazy-load-image-component";

import FiltersComp from "../../Common/Filters/OrderFilters";
import PaginatorCom from "../../Paginator/PaginatorCom";

import UsersModal from "../../Modals/UsersModal/UsersModal";
import StoreModal from "../../Modals/StoreModal/StoreModal";
import DeliverymanDetails from "../../../Pages/Users/Drivers/DeliverymanDetails";

const GridTable = ({
  isGrid,
  type,
  SetGridToggle,
  value,
  fetchAllData,
  FilterData,
  handlePagination,
}) => {
  const { theme } = useSelector((state) => state.theme); // true → light, false → dark

  const [toggleUsersDetails, setToggleUsersDetails] = useState(false);
  const [itemData, setItemData] = useState(null);

  const items = Array.isArray(value?.data?.user) ? value.data.user : [];

  const handleItemClick = (item) => {
    setToggleUsersDetails(true);
    setItemData(item);
  };

  const onPageChange = (e) => {
    if (e) {
      handlePagination?.({
        limit: e.rows,
        page: e.page + 1,
      });
    }
  };

  const activeFilters = FilterData?.filters?.filter((f) =>
    FilterData?.disabledFilters
      ? !FilterData.disabledFilters.includes(f.inputName)
      : true
  ) || [];

  return (
    <div
      className={`
        rounded-2xl
        ${theme ? "bg-white shadow-sm" : "bg-gray-900 shadow-xl"}
        p-4 sm:p-5 md:p-6
        min-h-[60vh] flex flex-col
      `}
    >
      {/* Filters */}
      <FiltersComp
        layoutView={true}
        gridToggle={isGrid}
        SetGridToggle={SetGridToggle}
        filters={activeFilters}
        SearchBy={
          FilterData?.SearchBy?.options?.length
            ? FilterData.SearchBy
            : { options: [] }
        }
        WeekRange={FilterData?.WeekRange ?? false}
        MonthRange={FilterData?.MonthRange ?? false}
        canlenderFilter={FilterData?.canlenderFilter}
        handleFilter={(name, value) => FilterData?.handleFilter?.(name, value)}
        handleClearFilter={() => FilterData?.handleClearFilter?.()}
        handleSearch={FilterData?.handleSearch ?? null}
        actionButton={
          FilterData?.button_name && FilterData?.handleButton
            ? {
                button_name: FilterData.button_name,
                handleButton: FilterData.handleButton,
              }
            : undefined
        }
      />

      {/* Main content area */}
      <div className="flex-1 mt-5 sm:mt-6">
        {items.length === 0 ? (
          <div
            className={`
              flex items-center justify-center h-64 text-center
              ${theme ? "text-gray-500" : "text-gray-400"}
            `}
          >
            No users found
          </div>
        ) : isGrid ? (
          // ─── GRID VIEW ───────────────────────────────────────────────────────
          <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6">
            {items.map((item) => (
              <div
                key={item._id || item.email || Math.random().toString()}
                onClick={() => handleItemClick(item)}
                className={`
                  group cursor-pointer rounded-xl overflow-hidden border transition-all duration-200
                  hover:shadow-md hover:-translate-y-0.5
                  ${
                    theme
                      ? "bg-white border-gray-200 hover:border-gray-300"
                      : "bg-gray-800 border-gray-700 hover:border-gray-600"
                  }
                `}
              >
                {/* Image container – responsive aspect ratio */}
                <div className={`relative w-full aspect-[4/3] sm:aspect-[5/4] overflow-hidden ${theme?"bg-gray-100":"bg-gray-800"}`}>
                  {item.profile_image ? (
                    <LazyLoadImage
                      src={item.profile_image}
                      alt={`${item.first_name || "User"} ${item.last_name || ""}`}
                      effect="blur"
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      wrapperProps={{ style: { transitionDelay: "0.6s" } }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <img
                        src={logo}
                        alt="placeholder"
                        className="w-14 h-14 sm:w-20 sm:h-20 opacity-70 object-contain"
                      />
                    </div>
                  )}
                </div>

                {/* Text content – better readability on small screens */}
                <div className="p-3 sm:p-4 space-y-2.5">
                  <h3
                    className={`
                      font-semibold text-base sm:text-lg truncate leading-tight
                      ${theme ? "text-gray-900" : "text-gray-100"}
                    `}
                  >
                    {item.first_name || ""} {item.last_name || "—"}
                  </h3>

                  <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
                    <span
                      className={`
                        px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap
                        ${
                          theme
                            ? "bg-green-100 text-green-800"
                            : "bg-green-900/50 text-green-300"
                        }
                      `}
                    >
                      {item.delivery_business?.name ||
                        (type === "users" ? "Customer" : "Business")}
                    </span>

                    <span
                      className={`
                        ${theme ? "text-gray-600" : "text-gray-400"}
                      `}
                    >
                      {item.gender || "—"}
                    </span>
                  </div>

                  <div
                    className={`
                      text-xs sm:text-sm space-y-1.5
                      ${theme ? "text-gray-600" : "text-gray-400"}
                    `}
                  >
                    <p className="truncate">{item.email || "—"}</p>
                    <p className="truncate">{item.phone_number || "—"}</p>
                    <p className="line-clamp-2">{item.address || "No address provided"}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // ─── LIST VIEW ───────────────────────────────────────────────────────
          <div className="space-y-4">
            {items.map((item) => (
              <div
                key={item._id || item.email || Math.random().toString()}
                onClick={() => handleItemClick(item)}
                className={`
                  flex flex-col sm:flex-row gap-4 p-4 rounded-xl border transition-all duration-200
                  hover:shadow-md cursor-pointer
                  ${
                    theme
                      ? "bg-white border-gray-200"
                      : "bg-gray-800 border-gray-700"
                  }
                `}
              >
                {/* Image - smaller on mobile */}
                <div className="w-full sm:w-32 md:w-36 lg:w-40 flex-shrink-0 aspect-[4/3] sm:aspect-auto sm:h-28 overflow-hidden rounded-lg">
                  {item.profile_image ? (
                    <LazyLoadImage
                      src={item.profile_image}
                      alt="profile"
                      effect="blur"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center ${theme?"bg-gray-100":"bg-gray-800"}`}>
                      <img
                        src={logo}
                        alt="placeholder"
                        className="w-16 h-16 opacity-70 object-contain"
                      />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 space-y-2.5">
                  <h3
                    className={`
                      font-semibold text-base sm:text-lg
                      ${theme ? "text-gray-900" : "text-gray-100"}
                    `}
                  >
                    {item.first_name || ""} {item.last_name || "—"}
                  </h3>

                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                    <div className={theme ? "text-gray-600" : "text-gray-400"}>
                      {item.gender || "—"}
                    </div>
                    <div className="truncate max-w-xs">
                      {item.email || "—"}
                    </div>
                    <div>{item.phone_number || "—"}</div>
                  </div>

                  <p
                    className={`
                      text-sm line-clamp-2
                      ${theme ? "text-gray-500" : "text-gray-400"}
                    `}
                  >
                    {item.address || "No address"}
                  </p>

                  <div className="pt-1">
                    <span
                      className={`
                        inline-flex px-3 py-1 text-xs font-medium rounded-full
                        ${
                          theme
                            ? "bg-green-100 text-green-800"
                            : "bg-green-900/50 text-green-300"
                        }
                      `}
                    >
                      {item.delivery_business?.name ||
                        (type === "users" ? "Customer" : "Business")}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-6 sm:mt-8 flex justify-center">
        <PaginatorCom
          paginatorData={{
            page: value?.data?.page || 1,
            rows: value?.data?.limit || 10,
            totalCount: value?.data?.totalCount || 0,
            totalPages: value?.data?.totalPages || 1,
          }}
          onPaginatorClick={onPageChange}
        />
      </div>

      {/* Modals */}
      {toggleUsersDetails && type === "users" && (
        <UsersModal
          itemData={itemData}
          setToggleUsersDetails={setToggleUsersDetails}
          toggleUsersDetails={toggleUsersDetails}
        />
      )}

      {toggleUsersDetails && type === "drivers" && (
        <DeliverymanDetails
          itemData={itemData}
          setToggleUsersDetails={setToggleUsersDetails}
          toggleUsersDetails={toggleUsersDetails}
        />
      )}

      {toggleUsersDetails && type === "store" && (
        <StoreModal
          itemData={itemData}
          setToggleUsersDetails={setToggleUsersDetails}
          toggleUsersDetails={toggleUsersDetails}
          fetchAllData={fetchAllData}
        />
      )}
    </div>
  );
};

export default GridTable;